import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import TimeSelect from '../components/TimeSelect';
import DateSelect from '../components/DateSelect';
import { getBookedEventDates, isDateBooked } from '../utils/bookingUtils';
import toast from '../utils/notifications';
import { AuthContext } from '../context/AuthContext';

const EventBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Helper function to split full name into first and last name
  const splitFullName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };
  
  const getUserName = () => {
    // Only auto-fill for customers, not staff or admin
    if (!user || user.role === 'staff' || user.role === 'admin') {
      return { firstName: '', lastName: '' };
    }
    // Prioritize first_name and last_name from API
    if (user.first_name || user.last_name) {
      return {
        firstName: user.first_name || '',
        lastName: user.last_name || ''
      };
    }
    // Fallback to splitting full_name or name
    if (user.full_name) return splitFullName(user.full_name);
    if (user.name) return splitFullName(user.name);
    return { firstName: '', lastName: '' };
  };
  
  const initialUserName = getUserName();
  
  const initialState = location.state?.eventDetails || {
    eventType: '',
    numberOfGuests: '',
    eventDate: '',
    eventTimeStart: '',
    eventTimeEnd: '',
    firstName: initialUserName.firstName,
    lastName: initialUserName.lastName,
    email: user?.email || '',
    phone: user?.phone || '',
    venue: 'Casa Gatchalian',
    paymentOption: ''
  };

  const [eventDetails, setEventDetails] = useState(initialState);

  // Auto-fill user details when user logs in or component mounts (customers only)
  useEffect(() => {
    if (user && !eventDetails.firstName && user.role === 'customer') {
      const userName = getUserName();
      setEventDetails(prev => ({
        ...prev,
        firstName: userName.firstName,
        lastName: userName.lastName,
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  // Refs for focusing fields when returning from payment page
  const dateRef = useRef(null);
  const timeStartRef = useRef(null);
  const timeEndRef = useRef(null);
  const guestsRef = useRef(null);
  const eventTypeRef = useRef(null);

  useEffect(() => {
    const focusTarget = location.state?.focus;
    if (!focusTarget) return;

    // For 'date' open modal instead of focusing native input
    if (focusTarget === 'date') {
      // small delay to allow route render
      setTimeout(() => setShowDateModal(true), 120);
      return;
    }

    // small delay to allow DOM to render and scroll smoothly
    setTimeout(() => {
      if (focusTarget === 'time' && timeStartRef.current) {
        timeStartRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        timeStartRef.current.focus();
      }
      if (focusTarget === 'guests' && guestsRef.current) {
        guestsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        guestsRef.current.focus();
      }
      if (focusTarget === 'eventType' && eventTypeRef.current) {
        eventTypeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        eventTypeRef.current.focus();
      }
    }, 150);
  }, [location.state]);

  // Fetch booked event dates on component mount
  useEffect(() => {
    const fetchBooked = async () => {
      const dates = await getBookedEventDates();
      setBookedDates(dates);
    };
    fetchBooked();
  }, []);

  const eventTypes = [
    'Weddings',
    'Anniversaries',
    'Birthdays',
    'Christenings',
    'Corporate Events',
    'Private Meetings',
    'Other Occasion'  
  ];

  const guestPackages = [
    { pax: 50, price: 'PHP 59,500.00' },
    { pax: 80, price: 'PHP 90,500.00' },
    { pax: 100, price: 'PHP 117,000.00' },
    { pax: 120, price: 'PHP 135,000.00' },
    { pax: 150, price: 'PHP 165,000.00' }
  ];

  const inclusions = [
    'Buffet Steward',
    'With band/Sound System',
    'Service Attendant',
    'Parking Attendant',
    '4hrs exclusive',
  ];

  const paymentOptions = [
    'Full Payment',
    'Down Payment'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate payment option is selected
    if (!eventDetails.paymentOption) {
      alert('Please select a payment option (Full Payment or Down Payment)');
      return;
    }
    
    // Navigate to payment confirmation page with event details
    navigate('/payment-confirmation', { state: { eventDetails } });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-5xl md:text-6xl mb-4 text-gray-900">WE CATER AND ACCEPT EVENTS</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Weddings, Anniversaries, Birthdays, Christenings, and Any Other Occasion
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-3xl font-semibold mb-8 text-gray-900">Book Event</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Event Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Event Type *</label>
                <select
                  ref={eventTypeRef}
                  name="eventType"
                  value={eventDetails.eventType}
                  onChange={handleChange}
                  required
                  className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                >
                  <option value="">Select an event type</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Number of Guests */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Guests *</label>
                <select
                  ref={guestsRef}
                  name="numberOfGuests"
                  value={eventDetails.numberOfGuests}
                  onChange={handleChange}
                  required
                  className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                >
                  <option value="">Select guest count</option>
                  {guestPackages.map(pkg => (
                    <option key={pkg.pax} value={pkg.pax}>{pkg.pax} pax</option>
                  ))}
                </select>
              </div>

              {/* Event Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Event Date *</label>
                <DateSelect
                  ref={dateRef}
                  value={eventDetails.eventDate}
                  onChange={val => {
                    const selectedDate = new Date(val);
                    if (isDateBooked(selectedDate, bookedDates)) {
                      toast.error('This date is not available for events');
                      return;
                    }
                    setEventDetails(prev => ({ ...prev, eventDate: val }));
                  }}
                  min={new Date().toISOString().slice(0, 10)}
                  bookedDates={bookedDates}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {bookedDates.length > 0 && `Unavailable dates: ${bookedDates.map(d => d.toLocaleDateString()).join(', ')}`}
                </p>
              </div>

              {/* Guest Info - Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={eventDetails.firstName}
                    onChange={handleChange}
                    required
                    placeholder="First name"
                    className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={eventDetails.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Last name"
                    className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={eventDetails.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={eventDetails.phone}
                    onChange={handleChange}
                    required
                    placeholder="(+63) Phone number"
                    className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-600"
                  />
                </div>
              </div>

              {/* Payment Options */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Options *</label>
                <div className="space-y-3">
                  {paymentOptions.map(option => (
                    <label key={option} className="flex items-center text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={eventDetails.paymentOption === option}
                        onChange={() => setEventDetails(prev => ({ 
                          ...prev, 
                          paymentOption: prev.paymentOption === option ? '' : option 
                        }))}
                        className="w-4 h-4 text-black border-gray-300 rounded focus:ring-gray-900 mr-3"
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors font-semibold"
                >
                  Proceed to Payment
                </button>
              </div>
            </form>
          </div>

          {/* Sidebar - Event Details */}
          <aside className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">Event Packages</h3>

            {/* Guest Packages */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-3">Packaged Idea</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                {guestPackages.map(pkg => (
                  <li key={pkg.pax} className="flex justify-between">
                    <span>{pkg.pax} pax</span>
                    <span className="font-medium">{pkg.price}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Inclusions */}
            <div className="mb-8">
              <h4 className="font-semibold text-gray-900 mb-3">Inclusions</h4>
              <ul className="space-y-2">
                {inclusions.map(item => (
                  <li key={item} className="flex items-center text-sm text-gray-700">
                    <CheckIcon className="w-4 h-4 text-black mr-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Additional Services */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Additional Services</h4>
              <p className="text-xs text-gray-600 mb-2">Master of Ceremony (MC)</p>
              <ul className="space-y-2">
                <li className="text-sm text-gray-700">• Mobile Bar</li>
                <li className="text-sm text-gray-700">• Grazing Table</li>
                <li className="text-sm text-gray-700">• Coffee Counter</li>
                <li className="text-sm text-gray-700">• Pool Party</li>
                <li className="text-sm text-gray-700">• Party Cart</li>
                <li className="text-sm text-gray-700">• Other Catering Service</li>
              </ul>
            </div>
          </aside>
        </div>
        {/* Contact Info removed per request */}

        {/* Date Modal (opens when user chooses "Change" from payment page) */}
        {showDateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowDateModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl mx-4">
              <div className="p-6 border-b">
                <h3 className="text-2xl font-semibold">Change date</h3>
              </div>
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Event Date</label>
                    <DateSelect
                      value={eventDetails.eventDate}
                      onChange={val => setEventDetails(prev => ({ ...prev, eventDate: val }))}
                      min={new Date().toISOString().slice(0, 10)}
                      bookedDates={bookedDates}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border-t flex justify-end space-x-3">
                <button
                  onClick={() => setShowDateModal(false)}
                  className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowDateModal(false)}
                  className="px-4 py-2 rounded-md bg-black text-white font-semibold hover:bg-gray-800"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBooking;
